import {
    getOSM,
    KPI_W,
    KPI_S,
    KPI_A,
    KPI_D,
    KPI_L,
    KPH_W,
    KPH_S,
    KPH_A,
    KPH_D,
} from '../../OverworldAnimation.js';
import ItemFactory from '../ItemFactory.js';

// BagPage enum - maps page names to indices
export const BagPage = {
    ITEMS: 0,
    BERRIES: 1,
    VITAMINS: 2,
    HELD_ITEMS: 3,
    POKEBALLS: 4,
    TMS_HMS: 5,
    KEY_ITEMS: 6,
    BATTLE_ITEMS: 7,
};

export default class BagMenuBackEnd {
    constructor({ allowedPages, stateMachine = getOSM() }) {
        this.stateMachine = stateMachine;
        this.currentPage = 0;
        this.maxBagPages = 8;
        this.currentOptionArray = [0, 0, 0, 0, 0, 0, 0, 0];
        this.maxOptionsArray = [0, 0, 0, 0, 0, 0, 0, 0];
        this.windowStartIndexArray = [0, 0, 0, 0, 0, 0, 0, 0];
        this.windowEndIndexArray = [5, 5, 5, 5, 5, 5, 5, 5];
        this.windowSize = 6;
        this.availableItems = [[], [], [], [], [], [], [], []];
        this.bagPageTitles = ['ITEMS', 'BERRIES', 'VITAMINS', 'HELD ITEMS', 'POKEBALLS', 'TMs & HMs', 'KEY ITEMS', 'BATTLE'];

        // Set allowed pages (default to all pages)
        this.allowedPages =
            allowedPages !== null && allowedPages !== undefined
                ? allowedPages
                : [
                      BagPage.ITEMS,
                      BagPage.BERRIES,
                      BagPage.VITAMINS,
                      BagPage.HELD_ITEMS,
                      BagPage.POKEBALLS,
                      BagPage.TMS_HMS,
                      BagPage.KEY_ITEMS,
                      BagPage.BATTLE_ITEMS,
                  ];

        // Ensure currentPage starts on a valid page
        if (!this.allowedPages.includes(this.currentPage)) {
            this.currentPage = this.allowedPages[0];
        }

        // Hold timing for vertical navigation
        this.verticalHoldFrames = 0;
        this.verticalInitialDelay = 30; // 30 frames before repeating
        this.verticalRepeatRate = 10; // Change every 10 frames after initial delay

        // Hold timing for horizontal navigation
        this.horizontalHoldFrames = 0;
        this.horizontalInitialDelay = 30; // 30 frames before repeating
        this.horizontalRepeatRate = 20; // Change every 20 frames after initial delay

        // Track which direction is being held
        this.currentVerticalDirection = null; // 'up' or 'down'
        this.currentHorizontalDirection = null; // 'left' or 'right'
    }

    update() {
        this.availableItems = getOSM().player.getAvailableItems();
        this.maxOptionsArray = this.availableItems.map((pageList) => pageList.length);
        this.currentOptionArray = this.currentOptionArray.map((currentOption, i) => {
            return Math.max(0, Math.min(currentOption, this.maxOptionsArray[i]));
        });
    }

    updateWindowPosition() {
        const currentIndex = this.currentOptionArray[this.currentPage];
        const start = this.windowStartIndexArray[this.currentPage];
        const itemCount = this.maxOptionsArray[this.currentPage];

        if (currentIndex === 0) {
            this.windowStartIndexArray[this.currentPage] = 0;
        }

        // Slide window down if selection is near bottom
        if (currentIndex + 2 > start + this.windowSize) {
            this.windowStartIndexArray[this.currentPage] = Math.min(
                currentIndex - this.windowSize + 2,
                itemCount - this.windowSize
            );
        }
        // Slide window up if selection is near top
        else if (currentIndex < start + 2 && currentIndex > 0) {
            this.windowStartIndexArray[this.currentPage] = Math.max(currentIndex - 1, 0);
        }

        // Reset window if page is empty
        if (itemCount === 0) {
            this.windowStartIndexArray[this.currentPage] = 0;
        }
    }

    getCurrentWindow() {
        const windowStart = this.windowStartIndexArray[this.currentPage];
        return this.availableItems[this.currentPage].slice(windowStart, windowStart + this.windowSize);
    }

    getIndexInCurrentWindow() {
        return Math.min(
            Math.max(
                this.currentOptionArray[this.currentPage] - this.windowStartIndexArray[this.currentPage],
                0
            ),
            this.currentOptionArray.length
        );
    }

    increaseVerticalOption() {
        const maxOptions = this.maxOptionsArray[this.currentPage];
        if (maxOptions === 0) return;
        this.currentOptionArray[this.currentPage] =
            (this.currentOptionArray[this.currentPage] + 1) % maxOptions;
    }

    decreaseVerticalOption() {
        const maxOptions = this.maxOptionsArray[this.currentPage];
        if (maxOptions === 0) return;
        this.currentOptionArray[this.currentPage] =
            (this.currentOptionArray[this.currentPage] - 1 + maxOptions) % maxOptions;
    }

    increaseHorizontalOption() {
        // Find current position in allowed pages array
        const currentIndexInAllowed = this.allowedPages.indexOf(this.currentPage);

        // Move to next allowed page (wrap around)
        const nextIndexInAllowed = (currentIndexInAllowed + 1) % this.allowedPages.length;
        this.currentPage = this.allowedPages[nextIndexInAllowed];
    }

    decreaseHorizontalOption() {
        // Find current position in allowed pages array
        const currentIndexInAllowed = this.allowedPages.indexOf(this.currentPage);

        // Move to previous allowed page (wrap around)
        const prevIndexInAllowed =
            (currentIndexInAllowed - 1 + this.allowedPages.length) % this.allowedPages.length;
        this.currentPage = this.allowedPages[prevIndexInAllowed];
    }

    handleVerticalInput() {
        let moved = false;

        // Handle UP input
        if (KPI_W) {
            // Initial press
            this.decreaseVerticalOption();
            this.currentVerticalDirection = 'up';
            this.verticalHoldFrames = 0;
            moved = true;
        } else if (KPH_W) {
            // Holding UP
            if (this.currentVerticalDirection === 'up') {
                this.verticalHoldFrames++;

                // After initial delay, start repeating
                if (this.verticalHoldFrames >= this.verticalInitialDelay) {
                    const framesAfterDelay = this.verticalHoldFrames - this.verticalInitialDelay;
                    if (framesAfterDelay % this.verticalRepeatRate === 0) {
                        this.decreaseVerticalOption();
                        moved = true;
                    }
                }
            }
        }

        // Handle DOWN input
        if (KPI_S) {
            // Initial press
            this.increaseVerticalOption();
            this.currentVerticalDirection = 'down';
            this.verticalHoldFrames = 0;
            moved = true;
        } else if (KPH_S) {
            // Holding DOWN
            if (this.currentVerticalDirection === 'down') {
                this.verticalHoldFrames++;

                // After initial delay, start repeating
                if (this.verticalHoldFrames >= this.verticalInitialDelay) {
                    const framesAfterDelay = this.verticalHoldFrames - this.verticalInitialDelay;
                    if (framesAfterDelay % this.verticalRepeatRate === 0) {
                        this.increaseVerticalOption();
                        moved = true;
                    }
                }
            }
        }

        // Reset if neither vertical key is held
        if (!KPH_W && !KPH_S && !KPI_W && !KPI_S) {
            this.currentVerticalDirection = null;
            this.verticalHoldFrames = 0;
        }

        return moved;
    }

    handleHorizontalInput() {
        let moved = false;

        // Handle LEFT input
        if (KPI_A) {
            // Initial press
            this.decreaseHorizontalOption();
            this.currentHorizontalDirection = 'left';
            this.horizontalHoldFrames = 0;
            moved = true;
        } else if (KPH_A) {
            // Holding LEFT
            if (this.currentHorizontalDirection === 'left') {
                this.horizontalHoldFrames++;

                // After initial delay, start repeating
                if (this.horizontalHoldFrames >= this.horizontalInitialDelay) {
                    const framesAfterDelay = this.horizontalHoldFrames - this.horizontalInitialDelay;
                    if (framesAfterDelay % this.horizontalRepeatRate === 0) {
                        this.decreaseHorizontalOption();
                        moved = true;
                    }
                }
            }
        }

        // Handle RIGHT input
        if (KPI_D) {
            // Initial press
            this.increaseHorizontalOption();
            this.currentHorizontalDirection = 'right';
            this.horizontalHoldFrames = 0;
            moved = true;
        } else if (KPH_D) {
            // Holding RIGHT
            if (this.currentHorizontalDirection === 'right') {
                this.horizontalHoldFrames++;

                // After initial delay, start repeating
                if (this.horizontalHoldFrames >= this.horizontalInitialDelay) {
                    const framesAfterDelay = this.horizontalHoldFrames - this.horizontalInitialDelay;
                    if (framesAfterDelay % this.horizontalRepeatRate === 0) {
                        this.increaseHorizontalOption();
                        moved = true;
                    }
                }
            }
        }

        // Reset if neither horizontal key is held
        if (!KPH_A && !KPH_D && !KPI_A && !KPI_D) {
            this.currentHorizontalDirection = null;
            this.horizontalHoldFrames = 0;
        }

        return moved;
    }

    manageInputs() {
        this.handleVerticalInput();
        this.handleHorizontalInput();

        this.updateWindowPosition();
        return KPI_L; // Return true if action button pressed
    }

    createItemFromCurrentOption() {
        const currentPageItems = this.availableItems[this.currentPage];
        const currentPageIndex = this.currentOptionArray[this.currentPage];
        const currentItemId = currentPageItems[currentPageIndex].id;
        return ItemFactory.create(currentItemId);
    }

    getCurrentPageTitle() {
        return this.bagPageTitles[this.currentPage];
    }

    setAllowedPages(allowedPages) {
        this.allowedPages = allowedPages;

        // If current page is not in the new allowed list, jump to first allowed page
        if (!this.allowedPages.includes(this.currentPage)) {
            this.currentPage = this.allowedPages[0];
        }
    }

    getAllowedPages() {
        return this.allowedPages;
    }
}
